import { Controller } from '@hotwired/stimulus';
import { scrollToCalendar } from '../mobile_scroll';

// Controller for calendar date/time selection
// Used in booking process step 2 (date/time selection)
// Receives availability data from server and renders time slots dynamically
export default class extends Controller {
  static targets = [
    'timeSlotsWrapper',
    'timeSlots',
    'timeSlotsList',
    'dateInput',
    'durationFilter',
    'durationInput'
  ];
  static values = {
    availability: { type: Array, default: [] },
    // Single-page flow: submit the form as soon as a time is picked (→ upsells). The multi-step
    // code path leaves this false and advances via the explicit "Weiter" button instead.
    autoSubmit: { type: Boolean, default: false },
    // Localized by the server: ES modules cannot reach the Rails translations.
    noTimesAvailableText: String,
    durationOptions: { type: Array, default: [] },
    selectedDuration: { type: Number, default: 0 }
  };

  connect() {
    this.selectedDate = null;
    this.availabilityMap = this.buildAvailabilityMap();
    this.activeDuration = this.selectedDurationValue || null;
    this.syncDurationFilterUi();
    this.persistDurationMinutes(this.activeDuration);

    // Event delegation for dynamically added time slot radio buttons
    this.element.addEventListener('change', (e) => this.handleTimeChange(e));

    // Mobile: the calendar has just been revealed — anchor it in view.
    scrollToCalendar();
  }

  handleTimeChange(event) {
    const radio = event.target;
    if (radio.type === 'radio' && radio.name.includes('[time]')) {
      this.selectTime({ currentTarget: radio });
    }
  }

  // Build a map of date -> slot objects { time, durations } for quick lookup
  buildAvailabilityMap() {
    const map = {};
    this.availabilityValue.forEach((day) => {
      map[day.date] = (day.times || []).map((entry) => this.normalizeSlot(entry));
    });
    return map;
  }

  normalizeSlot(entry) {
    if (typeof entry === 'string') {
      return { time: entry, durations: this.durationOptionsValue };
    }
    return {
      time: entry.time,
      durations: Array.isArray(entry.durations) ? entry.durations.map(Number) : this.durationOptionsValue
    };
  }

  selectDate(event) {
    const button = event.currentTarget;
    const date = button.dataset.date;

    if (!date || button.disabled) return;

    // Deselect all other dates
    this.element.querySelectorAll('.calendar__cell-button').forEach((btn) => {
      btn.setAttribute('aria-selected', 'false');
      btn.classList.remove('selected');
    });

    // Select this date
    button.setAttribute('aria-selected', 'true');
    button.classList.add('selected');
    this.selectedDate = date;

    // On mobile the month grid collapses into one horizontally scrollable day strip once a day is
    // picked (CSS keys off this class); desktop keeps the full grid alongside the time column.
    this.element.querySelector('.calendar')?.classList.add('calendar--day-selected');

    // Update hidden input
    if (this.hasDateInputTarget) {
      this.dateInputTarget.value = date;
    }

    // Only render time slots dynamically if we have availability data
    // Otherwise, the static time slots from the server are already rendered
    if (this.availabilityValue.length > 0) {
      this.renderTimeSlots(date);
    }

    // Show time slots panel
    if (this.hasTimeSlotsTarget) {
      this.timeSlotsTarget.classList.remove('hidden');
    }

    // Mobile: center the picked day in the compact strip, and anchor the calendar in view.
    this.scrollStripToSelected(button);
    scrollToCalendar();
  }

  // Mobile compact view: horizontally scroll the collapsed day strip so the selected day is centered.
  scrollStripToSelected(button) {
    if (!window.matchMedia('(max-width: 639px)').matches) return;

    requestAnimationFrame(() => {
      const strip = this.element.querySelector('.calendar__body');
      if (!strip || strip.scrollWidth <= strip.clientWidth) return;

      const stripRect = strip.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const delta = buttonRect.left + buttonRect.width / 2 - (stripRect.left + stripRect.width / 2);
      strip.scrollBy({ left: delta, behavior: 'smooth' });
    });
  }

  filterDuration(event) {
    const button = event.currentTarget;
    const minutes = Number(button.dataset.durationMinutes);
    this.activeDuration = Number.isFinite(minutes) && minutes > 0 ? minutes : null;
    this.syncDurationFilterUi();
    this.persistDurationMinutes(this.activeDuration);

    if (this.selectedDate) {
      this.renderTimeSlots(this.selectedDate);
    }
  }

  syncDurationFilterUi() {
    if (!this.hasDurationFilterTarget) return;

    this.durationFilterTarget.querySelectorAll('[data-duration-minutes]').forEach((button) => {
      const minutes = Number(button.dataset.durationMinutes);
      const active = this.activeDuration === minutes || (this.activeDuration == null && !minutes);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  persistDurationMinutes(minutes) {
    if (!this.hasDurationInputTarget) return;
    this.durationInputTarget.value = minutes || '';
  }

  renderTimeSlots(date) {
    if (!this.hasTimeSlotsListTarget) return;

    const allSlots = this.availabilityMap[date] || [];
    let slots = this.filteredSlots(allSlots);

    // Day tiles mark "available" from base length. If a longer filter (e.g. leftover 45 from
    // upsells) matches nothing that day, fall back to the shortest length that still fits.
    if (slots.length === 0 && this.activeDuration && allSlots.length > 0) {
      const fallback = this.durationOptionsValue.find((minutes) =>
        allSlots.some((slot) => slot.durations.includes(minutes))
      );
      if (fallback) {
        this.activeDuration = fallback;
        this.syncDurationFilterUi();
        this.persistDurationMinutes(this.activeDuration);
        slots = this.filteredSlots(allSlots);
      } else {
        slots = allSlots;
      }
    }

    if (slots.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'calendar__empty';
      empty.textContent = this.noTimesAvailableTextValue;
      this.timeSlotsListTarget.innerHTML = empty.outerHTML;
      return;
    }

    const html = slots.map((slot) => this.slotMarkup(slot)).join('');
    this.timeSlotsListTarget.innerHTML = html;
  }

  filteredSlots(slots) {
    if (!this.activeDuration) return slots;
    const minutes = Number(this.activeDuration);
    return slots.filter((slot) => slot.durations.includes(minutes));
  }

  slotMarkup(slot) {
    const durationsMarkup = this.durationOptionsValue.length
      ? `<span class="booking-time-slot__durations" aria-hidden="true">${this.durationOptionsValue
          .map((minutes) => {
            const available = slot.durations.includes(minutes);
            return `<span class="booking-time-slot__duration${available ? '' : ' is-unavailable'}">${minutes}</span>`;
          })
          .join('<span class="booking-time-slot__duration-sep"> / </span>')}<span class="booking-time-slot__duration-unit"> Min.</span></span>`
      : '';

    return `
      <label class="booking-time-slot">
        <input type="radio"
               name="form[time]"
               value="${slot.time}"
               data-action="change->booking-calendar#selectTime">
        <span class="booking-time-slot__label">${slot.time}</span>
        ${durationsMarkup}
      </label>
    `;
  }

  selectTime(event) {
    const radio = event.currentTarget;
    const time = radio.value;

    // Combine date and time into starts_at
    if (this.selectedDate && time) {
      // Search in the whole form, not just controller element
      const form = this.element.closest('form');
      const startsAtInput =
        form?.querySelector('input[name*="starts_at"]') ||
        this.element.querySelector('input[name*="starts_at"]');

      if (startsAtInput) {
        startsAtInput.value = `${this.selectedDate} ${time}`;
      }

      // Keep the active duration filter as the booked length (fallback: shortest available on slot).
      const slot = (this.availabilityMap[this.selectedDate] || []).find((entry) => entry.time === time);
      const minutes =
        this.activeDuration && slot?.durations.includes(this.activeDuration)
          ? this.activeDuration
          : slot?.durations?.[0] || this.durationOptionsValue[0] || null;
      this.persistDurationMinutes(minutes);

      // Single-page flow: picking a time is the final step — submit straight to upsells.
      if (this.autoSubmitValue && form) {
        form.requestSubmit();
      }
    }
  }
}
